/**
 * clinic.ts — the clinician assessment reads + writes (authed CLINICIAN /
 * ADMIN): the structured review, the staff fill-on-behalf create, assessment
 * clinical notes, the plan-link handover, the deal drawer's callback activity
 * writes, the money-free analytics reads and the G2 audit read.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the review view needs the SUBMITTED assessment projected into readable
 * sections (eligibility / safety / weight / meds / preference / consent) with
 * BMI, the soft-stop flags, prior treatment, the weight-photo ref and the
 * SCR-check consent status — NEVER the raw versioned answers Json. The clinic
 * service owns the projection (it holds the answers + the R28 med content); the
 * portal only renders the already-structured shape.
 *
 * The standalone callbacks-ticketing UI (list/detail/status/notes/close) and
 * the staff submissions list retired with the CRM pipeline (2026-07-21) — the
 * ticket lifecycle is server-side and its surviving portal surface is the deal
 * drawer's ActivityComposer, so only the called-marker PATCH and the activity
 * POST remain here.
 */
import { get, patch, post, queryString, request } from './client';
import type {
  AssessmentCreateRequest,
  AssessmentRecommendation,
  AssessmentRecommendationRead,
  PatientAssessmentRecommendationRead,
  AssessmentPhotosRead,
  AssessmentReview,
  ApprovePlanResult,
  AssessmentSummary,
  AssessmentSubmitResult,
  AuditListResponse,
  AuditQuery,
  CallbackActivity,
  CallbackActivityCreateRequest,
  CallbackSubmission,
  CaseNote,
  CaseNoteList,
  HandoverResult,
  StaffAnswerResult,
  StaffAnswerSubmitRequest,
  ActivityHeatmap,
  ClinicianCallbackStats,
  ClinicianMetrics,
  DropOffReasonCounts,
  PhotoUploadGrant,
  WeightEvidenceRetractResult,
  WeightEvidenceRequestResult,
} from './types';

export interface NotificationRequestRow {
  id: string;
  name: string;
  email: string;
  serviceKey: string;
  serviceName: string;
  pageUrl: string;
  createdAt: string;
}

export interface NotificationRequestList {
  items: NotificationRequestRow[];
  nextCursor: string | null;
}

/** ADMIN-only upcoming-service expressions of interest, newest first. */
export function getNotificationRequests(params: {
  serviceKey?: string;
  search?: string;
  limit?: number;
  before?: string;
} = {}): Promise<NotificationRequestList> {
  return get(`/v1/clinic/notify-requests${queryString(params)}`, true);
}

/** GET /v1/clinic/assessments/:id → the structured clinician review. */
export function getAssessmentReview(assessmentId: string): Promise<AssessmentReview> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}`, true);
}

/**
 * GET /v1/clinic/assessments/:id/photos → EVERY stored weight photo as a
 * short-lived signed READ url (CLINICIAN/ADMIN, 2026-08-27).
 *
 * WHY a second read rather than a widened review: the pinned contracts
 * `assessmentReview` is strict and its single `photoUrl` stays exactly as it
 * is, so the gallery ships beside it (the clinic sibling made the same call).
 * The urls EXPIRE — call this again to re-mint them; never retry a dead link.
 */
export function getAssessmentPhotos(assessmentId: string): Promise<AssessmentPhotosRead> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/photos`, true);
}

/* ── Weight self-certification (clinic #166, 2026-09-04) ─────────────────────
 *
 * q14 no longer asks for a photo: it carries a DECLARATION the patient ticks.
 * Requiring an image from every patient upfront took throughput from ~25
 * leads/week to ~2, and Reneal Patel approved removing the UPFRONT requirement
 * on condition that the self-certification is robust. VERIFICATION IS NOT
 * REMOVED — it becomes clinician-driven, and these are the two staff lanes it
 * runs on.
 *
 * The CTO's frame, which both of these are shaped by: A WINDOW, NOT A GATE.
 * Evidence may be added by the patient, an admin or a clinician at any time up
 * to the second dispense, and orders-service refuses that dispense ONLY where a
 * request went unanswered. Nothing here rebuilds a universal mandatory step.
 */

/**
 * POST /v1/clinic/assessments/:id/photo-upload/staff → a signed PUT grant so a
 * CLINICIAN or an ADMIN can add weight evidence to a named patient's file
 * ("anyone must be able to add a photo" — CTO, 2026-09-04).
 *
 * The SAME grant → signed-PUT → ledger seam as the guest and patient lanes, and
 * deliberately a DISTINCT path: `/staff` keeps it out of the gateway's @Public
 * allowlist, so it arrives at clinic-service with the VERIFIED role — which is
 * what lets the `AssessmentPhoto` row record WHO obtained the evidence
 * (`addedByRole`, an enum, never a name — R19). No `?clinicSlug=`: the verified
 * role is the bind, not possession of a slug.
 *
 * ⚠️ THE SERVER IS THE CONTROL (R41), AND ITS REFUSALS ARE THE USEFUL PART.
 * It re-counts under a row lock and 409s past the five-photo cap, 409s a
 * CLIN-03 sticky stop, and 404s a purged row — all of which the shared upload
 * machine already treats as a permanent close (`WeightPhotoUploads`). The cap
 * is spent at GRANT time, so a granted-then-failed PUT has still consumed one.
 */
export function grantStaffPhotoUpload(assessmentId: string): Promise<PhotoUploadGrant> {
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/photo-upload/staff`,
    {},
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/assessments/:id/evidence-request → ask this patient to
 * evidence the weight they self-certified (CLINICIAN·ADMIN).
 *
 * NO BODY, and that is the server's design rather than an omission: the actor
 * is the verified principal and the target is the path param, so there is
 * nothing a caller could usefully — or safely — supply.
 *
 * It appends the durable `WeightEvidenceRequest` row orders-service consults
 * before a SECOND dispense (and refuses only where the request went
 * unanswered), plus a `weight_evidence_requested` audit row, in one
 * transaction; the neutral `photo_request` email goes out after the commit. A
 * repeat call is a CHASE — it appends a second row and sends again, because the
 * audit needs to see that we asked twice.
 *
 * `parseError` because the 409 is the one message that tells the caller what
 * happened: a draft / rejected / stopped journey is refused outright, since a
 * request that can never be answered must not exist. PHI-free by construction.
 */
export function requestWeightEvidence(assessmentId: string): Promise<WeightEvidenceRequestResult> {
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/evidence-request`,
    {},
    { auth: true, parseError: true },
  );
}

/**
 * DELETE /v1/clinic/assessments/:id/photos/:photoId (**ADMIN or CLINICIAN**,
 * clinic-service 2026-09-06) — RETRACT one stored weight photo. The CTO's ask:
 * *"give the ability to remove it in case someone made a mistake"*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ THIS DESTROYS ARTICLE 9 CLINICAL EVIDENCE, PERMANENTLY. There is no undo,
 * no soft-delete and no recycle bin: the object leaves the bucket, the row
 * leaves the database, and the audit entry becomes the ONLY record that the
 * image ever existed. Every caller must confirm first, in words that say what
 * is lost.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `reason` is **REQUIRED** (3..2000, trimmed server-side) and this is the one
 * place the estate departs from its own "a forced note produces filler" rule
 * (mark-test, mark-duplicate). Those actions have one obvious meaning; this one
 * does not. "Wrong patient", "unreadable", "uploaded twice" and "the patient
 * asked us to take it down" are four different findings with four different
 * follow-ups — one of them a reportable personal-data breach — and once the
 * image is gone there is nothing else left to derive it from.
 *
 * WHO. **STAFF — ADMIN or CLINICIAN**, server-enforced
 * (`@Roles('ADMIN', 'CLINICIAN')`). A PATIENT must not destroy the evidence
 * behind a supply that has already happened: their lane is a DPO erasure
 * request with an Article 9 balancing test (UK GDPR Art. 17(3)(b)/(h)), not a
 * button.
 *
 * ⚠️ CORRECTED 2026-09-06 — THIS SAID "ADMIN only … the portal must therefore
 * withhold the control from a CLINICIAN viewer". It was true for part of one
 * day. The CTO widened the guard, over a raised objection, and this note is the
 * kind a reader trusts when deciding what the client is allowed to offer — left
 * stale it would have argued them into removing a clinician's control and
 * calling it a fix.
 *
 * ⚠️ THE OBJECTION, KEPT BECAUSE IT WAS WEIGHED AND NOT MISSED: a CLINICIAN is
 * the JUDGE of this evidence, so letting the person who signs "I reviewed these
 * photos" also decide which ones exist puts the attestation and the record it
 * is made against in one pair of hands, and the clinician-adds/admin-retracts
 * hand-off WAS the two-person control. It was overruled because the case that
 * actually happens is a clinician finding ANOTHER PATIENT'S photo on a file —
 * a live personal-data breach that queuing behind an admin only prolongs. The
 * replacement control is the audit trail: the verified actor, their role, and a
 * mandatory reason, on an append-only row. Full reasoning on clinic-service's
 * `retractPhoto` and in `ReviewView`'s block above `photoGrid`.
 *
 * `parseError` because every refusal is a sentence staff can act on: 409 once
 * the linked order is DISPATCHED (the evidence underwrote a supply that
 * happened), 404 on an unknown/purged assessment or a photo that is not this
 * assessment's, 503 when the bucket cannot confirm the delete — in which case
 * **nothing was removed** and the photo is still on file, which is the safe
 * direction and the message says so.
 *
 * The five-photo slot COMES BACK: the server counts stored photos rather than
 * decrementing a counter, so `photosRemaining` in the response is both the true
 * size of the file and the number the next upload's cap check will see.
 */
export function retractWeightPhoto(
  assessmentId: string,
  photoId: string,
  reason: string,
): Promise<WeightEvidenceRetractResult> {
  return request(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/photos/${encodeURIComponent(photoId)}`,
    // A BODY ON A DELETE, which is unusual and deliberate: the estate already
    // does it for the other destructive staff lane (identity's user-delete
    // carries `{reason}`), and the gateway forwards a parsed JSON body on every
    // method except GET/HEAD — so there is no proxy-shaped reason to invent a
    // POST spelling of a deletion.
    { method: 'DELETE', body: { reason }, auth: true, parseError: true },
  );
}

/** PMS data-minimised summary (item 3) — state/eligibility/BMI/section title+count
 *  only. Backs the patient-record drawer's Questionnaires panel (no over-fetch). */
export function getAssessmentSummary(assessmentId: string): Promise<AssessmentSummary> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/summary`, true);
}

/* ── Money-free analytics (CLINICIAN·ADMIN, 2026-07-21) ───────────────────────
 * COUNT reads over the callback activity log + audit trail — no money, so safe
 * on the clinician surface. See clinic-service src/analytics/.
 */

/** GET /v1/clinic/analytics/me → my callback stats + daily trend (money-free). */
export function getMyCallbackStats(days?: number): Promise<ClinicianCallbackStats> {
  return get(`/v1/clinic/analytics/me${queryString({ days })}`, true);
}

/** GET /v1/clinic/analytics/activity → call-volume heatmap, optionally per clinic (money-free). */
export function getCallbackActivity(days?: number, clinicSlug?: string): Promise<ActivityHeatmap> {
  return get(`/v1/clinic/analytics/activity${queryString({ days, clinicSlug })}`, true);
}

/** GET /v1/clinic/analytics/clinicians → per-clinician callback rollup (ADMIN). */
export function getClinicianMetrics(days?: number): Promise<ClinicianMetrics> {
  return get(`/v1/clinic/analytics/clinicians${queryString({ days })}`, true);
}

/**
 * GET /v1/clinic/analytics/drop-off-reasons[?days=] (ADMIN, T-2 2026-09-11) —
 * why approved patients have not paid, ranked. Counts per reason only (R19); the
 * server zero-fills and ranks. The same `?days=` window as the reads above.
 */
export function getDropOffReasonCounts(days?: number): Promise<DropOffReasonCounts> {
  return get(`/v1/clinic/analytics/drop-off-reasons${queryString({ days })}`, true);
}

/* ── Assessment clinical notes (CLINICIAN/ADMIN, 2026-07-20) ──────────────────
 *
 * The pre-checkout note trail a clinician keeps while filling a form on a
 * patient's behalf (only an assessmentId exists then — no orderCaseId). Same
 * wire shape as the OrderCase CaseNote; server-sanitised, author-only edit.
 */

/** GET /v1/clinic/assessments/:id/notes → the assessment's note trail, newest first. */
export function getAssessmentNotes(assessmentId: string): Promise<CaseNoteList> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/notes`, true);
}

/**
 * POST /v1/clinic/assessments/:id/notes → add a note (author = current user,
 * server-sanitised). `questionKey` (Wave 4, optional) ANCHORS the note to one
 * questionnaire answer — the server validates the key against the assessment's
 * question set; omitted = a general clinical note.
 */
export function addAssessmentNote(assessmentId: string, body: string, questionKey?: string): Promise<CaseNote> {
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/notes`,
    { body, ...(questionKey ? { questionKey } : {}) },
    { auth: true },
  );
}

/** PATCH /v1/clinic/assessments/:id/notes/:noteId → edit YOUR OWN note (server re-checks authorship; stamps editedAt). */
export function editAssessmentNote(assessmentId: string, noteId: string, body: string): Promise<CaseNote> {
  return patch(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/notes/${encodeURIComponent(noteId)}`, { body });
}

/** PATCH /v1/clinic/callbacks/:id/called → record the date the contact was called. */
export function markCallbackCalled(callbackId: string, calledAt: string): Promise<CallbackSubmission> {
  return patch(`/v1/clinic/callbacks/${encodeURIComponent(callbackId)}/called`, { calledAt });
}

/**
 * POST /v1/clinic/assessments/staff → a CLINICIAN fills the draft-assessment
 * engine on a patient's behalf (2026-07-20 — moved from ADMIN; see
 * src/pipeline/QuestionnairePanel.tsx, the retired ClinicianFormPage's
 * replacement, PR 4/5). Same request/response shape as the guest journey's
 * `createAssessment` (journey.ts), but AUTHED and on a different path: the
 * gateway only anonymizes the guest path, so this one reaches clinic-service
 * with the verified CLINICIAN role and origin stamps `'clinician'`.
 */
export function createStaffAssessment(body: AssessmentCreateRequest): Promise<{ assessmentId: string }> {
  return post('/v1/clinic/assessments/staff', body, { auth: true });
}

/**
 * POST /v1/clinic/assessments/:id/answers/staff → the CLINICIAN deal-drawer
 * answer (2026-08-06 hard-stop safety net). Same per-answer engine as the guest
 * `submitAnswer` (journey.ts) but AUTHED on the `/staff` dual path, so
 * clinic-service sees the VERIFIED CLINICIAN role. CONFIRM-BEFORE-LOST: a
 * disqualifying answer without `confirmClose` returns `wouldCloseLost: true` and
 * does NOT close the lead — the caller then confirms (resubmitting the SAME body
 * with `confirmClose: true`) or revises the answer. The response is the
 * `EvaluationResult` widened with `wouldCloseLost` + `questionKey`.
 */
export function submitStaffAnswer(
  assessmentId: string,
  body: StaffAnswerSubmitRequest,
): Promise<StaffAnswerResult> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/answers/staff`, body, { auth: true });
}

/**
 * POST /v1/clinic/assessments/:id/submit/staff → the STAFF submit of the deal
 * drawer's single-page form (UX Wave 9 §4). Same gates/result as the guest
 * `submitAssessment` (journey.ts) but AUTHED on the `/staff` dual path, so
 * clinic-service sees the verified CLINICIAN role and writes the
 * `assessment_submitted` audit row (the "My activity" source for forms the
 * clinician completed) inside the submit transaction.
 */
export function submitStaffAssessment(assessmentId: string): Promise<AssessmentSubmitResult> {
  return post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/submit/staff`, {}, { auth: true });
}

/**
 * POST /v1/clinic/assessments/:id/handover → email the patient their "choose
 * your plan" link for a submitted assessment. CLINICIAN·ADMIN since 2026-09-04
 * (ADMIN→CLINICIAN 2026-07-20, and now both): the patient-recovery exercise put
 * the back office on the phone to ~20 clinically approved patients who never
 * picked a plan or never paid, and re-sending a link the clinician's approval
 * already earned is not a prescribing decision.
 *
 * EVERY SUCCESSFUL CALL SENDS A REAL EMAIL. There is no "already handed over"
 * precondition and no idempotency key — a second call is a second email, which
 * is why the callers gate this behind a confirm dialog and their own in-flight
 * guard rather than treating it as a re-fetch.
 *
 * THE PLAN BODY IS CLINICIAN-ONLY (403 for an ADMIN caller, 2026-09-04): picking
 * the medication IS the prescribing decision. Never pass `plan` from an admin
 * surface — the server decides that BEFORE any DB read, so the 403 also fires
 * for an assessment id that does not exist and proves nothing about the record.
 * A bare body is the plain re-send, and that is the whole of what an admin may do.
 *
 * Errors the callers code against, all PHI-free and safe to surface:
 *   404 unknown assessment; 409 not submitted/eligible; 409 no lead email on
 *   file; 400 a plan on a patient-filled assessment; 403 the plan/role rule
 *   above; 429 the rolling 24h send cap (10 per assessment, counted across BOTH
 *   roles — a clinician's earlier sends spend the same budget an admin sees).
 * `parseError` is what lands those bodies' `message` on `ApiError.detail`.
 */
export function sendPlanHandover(
  assessmentId: string,
  plan?: { productKey: string; months: number },
): Promise<HandoverResult> {
  // A plan (2026-07-20, clinician-led lane) pre-approves it in orders-service
  // before the email; an empty body is the plain resend. Both-or-neither.
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/handover`,
    plan ? { productKey: plan.productKey, months: plan.months } : {},
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/assessments/:id/approve-plan — THE CLINICIAN'S APPROVAL
 * (2026-09-07). **CLINICIAN-only.**
 *
 * The assessment is submitted for clinical review BEFORE checkout, so a
 * clinician's approval and a patient's payment are independent events. When the
 * patient has already authorised their card for exactly this plan, the server
 * captures month 1, issues the prescription and sends it to the pharmacy;
 * otherwise it emails the plan link, exactly as `sendPlanHandover` always has.
 *
 * ⚠️ THIS FUNCTION DOES NOT CHOOSE, AND MUST NEVER BE MADE TO (R41). There is no
 * argument here that names a medication or asks for a charge: what the clinician
 * approved is read server-side from the recommendation set they just issued, and
 * the money decision is made by orders-service, which owns the authorisation. A
 * portal that picked the endpoint could be made to charge a patient by anyone who
 * can call the API.
 *
 * ⚠️ IT IS A DIFFERENT ENDPOINT FROM `sendPlanHandover`, ON PURPOSE. That one is
 * the plain RE-SEND (the pipeline drawer's "Send plan link") and must never
 * charge anybody, so it stays exactly as it was. Do not merge the two.
 *
 * ⚠️ THE CALLER MUST BRANCH ON `outcome` — see `ApprovePlanResult`. Reporting
 * "emailed to the patient" for `charged` tells a clinician something untrue
 * about a patient's money.
 *
 * `months` is the term the clinician is approving and is normally omitted: on
 * this surface they recommend medications and the patient's own term stands.
 *
 * `recommendationId` is the id the RECOMMENDATION SAVE just returned, and it is
 * an optimistic-concurrency token rather than an instruction (2026-09-07). The
 * approval is two calls, and the server reads the CURRENT recommendation — so
 * two clinicians on one assessment can cross in that gap and the first one's
 * approval would capture and dispatch the second one's medication list under the
 * first one's name. Sending it makes the server 409 instead. ⚠️ It cannot steer
 * WHICH plan is charged for: the server compares, it never looks the id up, so a
 * stale or invented value can only produce a refusal.
 *
 * Errors the callers code against, all PHI-free and safe to surface:
 *   404 unknown assessment; 409 not submitted/eligible; 403 a non-clinician
 *   caller; 429 the rolling 24h send cap on the link lane; **503 when
 *   orders-service could not be reached** — deliberately fail-closed, because a
 *   lost response may be a capture that happened, so nothing was sent and the
 *   clinician should retry rather than assume.
 * `parseError` is what lands those bodies' `message` on `ApiError.detail`.
 */
export function approveClinicianPlan(
  assessmentId: string,
  opts: { months?: number; recommendationId?: string } = {},
): Promise<ApprovePlanResult> {
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/approve-plan`,
    {
      ...(opts.months === undefined ? {} : { months: opts.months }),
      ...(opts.recommendationId === undefined ? {} : { recommendationId: opts.recommendationId }),
    },
    { auth: true, parseError: true },
  );
}

/**
 * POST /v1/clinic/assessments/:id/plan-link-copied — TELL THE SERVER A HUMAN
 * TOOK THE LINK (2026-09-06). Writes one `assessment_plan_link_copied` audit
 * row: ids and the caller's verified role, no reason, no context, no email, no
 * state change. It never moves `planLinkLastSentAt`, the `planSent` chip or the
 * send cap — nothing was sent.
 *
 * ⚠️ FIRE AND FORGET, AND THE SIGNATURE IS THE ENFORCEMENT. This returns
 * `void`, not a Promise, so a caller CANNOT await it, cannot branch on it and
 * cannot render a toast off it. That is the whole contract: the link reaches
 * the clipboard FIRST and this goes out AFTER, best-effort. A human who
 * successfully copied a link and then got an error banner about telemetry has
 * been told their copy failed, which is both confusing and false — and blocking
 * the copy on a network call would break the one thing they came for.
 *
 * Every rejection is swallowed here rather than at the call site, so there is
 * exactly one place the rule lives. A 429 (30 copies per assessment per rolling
 * 24h) is swallowed like anything else: the cap protects the audit table, and
 * it is not the copier's problem.
 *
 * ⚠️⚠️ THE RESULTING COUNT IS A FLOOR, NEVER A COUNT, and nothing downstream may
 * read it as one. A dropped request, an offline desk, a link pasted out of an
 * email, a screenshot read aloud over the phone — none of them write a row.
 * THE ABSENCE OF A ROW IS EVIDENCE OF NOTHING.
 */
export function recordPlanLinkCopied(assessmentId: string): void {
  void post(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/plan-link-copied`, {}, { auth: true }).catch(
    () => {
      /* Deliberately empty — see the fire-and-forget rule above. There is no
         failure of this call that a person copying a link can or should act on. */
    },
  );
}

/**
 * POST /v1/clinic/callbacks/:id/activities → log a note / call attempt /
 * email on the ticket's timeline (v0.6.5) — the deal drawer's ActivityComposer
 * write; the logged entry surfaces on the deal's merged activity feed.
 */
export function addCallbackActivity(callbackId: string, body: CallbackActivityCreateRequest): Promise<CallbackActivity> {
  return post(`/v1/clinic/callbacks/${encodeURIComponent(callbackId)}/activities`, body, { auth: true });
}

/* ── Assessment recommendations (UX Wave 4, 2026-07-21) ───────────────────────
 *
 * [CONTRACTS GAP] The clinician's persisted medication recommendation set for a
 * SUBMITTED assessment. Keys only — no price rides these shapes, so the whole
 * lane is safe on the money-free clinician surface.
 *
 * Staff read (`getAssessmentRecommendation`) stays CLINICIAN/ADMIN-shaped.
 * Patient plan filter uses `getPatientAssessmentRecommendation` — a dedicated
 * PATIENT-safe path that never weakens the staff endpoint.
 */

/** GET /v1/clinic/assessments/:id/recommendations → staff set, or `{recommendation: null}`. */
export function getAssessmentRecommendation(assessmentId: string): Promise<AssessmentRecommendationRead> {
  return get(`/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/recommendations`, true);
}

/**
 * GET /v1/clinic/assessments/:id/patient-recommendations — PATIENT ownership
 * + optional clinicSlug. Opaque productKeys only (or null = Route 1 catalogue).
 * A `checkoutToken` uses the dedicated guest lane
 * (`GET /v1/clinic/checkout/guest/patient-recommendations`) so the PATIENT URL
 * keeps its bearer. Never send a guest grant at the PATIENT path — and never
 * call the guest lane WITHOUT one: the gateway 401s a tokenless request and
 * clinic 404s an unbound grant, because the stamped guest principal is derived
 * from the assessment id and so proves nothing by itself.
 */
export function getPatientAssessmentRecommendation(
  assessmentId: string,
  clinicSlug: string,
  opts?: { checkoutToken?: string },
): Promise<PatientAssessmentRecommendationRead> {
  const q = new URLSearchParams({ clinicSlug });
  if (opts?.checkoutToken) {
    q.set('assessmentId', assessmentId);
    return request(`/v1/clinic/checkout/guest/patient-recommendations?${q}`, {
      method: 'GET',
      headers: {
        'x-checkout-token': opts.checkoutToken,
        'x-assessment-id': assessmentId,
      },
    });
  }
  return get(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/patient-recommendations?${q}`,
    true,
  );
}

/**
 * POST /v1/clinic/assessments/:id/recommendations (CLINICIAN) → upsert-latest
 * {productKeys: 1..4}. The server 409s unless the assessment is submitted +
 * eligible/clinician_review — `parseError` so the 409 body's message reaches
 * the UI via `ApiError.detail` (same surface rule as the handover).
 */
export function saveAssessmentRecommendation(
  assessmentId: string,
  productKeys: string[],
): Promise<AssessmentRecommendation> {
  return post(
    `/v1/clinic/assessments/${encodeURIComponent(assessmentId)}/recommendations`,
    { productKeys },
    { auth: true, parseError: true },
  );
}

/**
 * GET /v1/clinic/admin/audit?limit&before → the clinic-service audit trail
 * (ADMIN, G2): callback status changes / calls / closes, assessment handovers
 * and deal lifecycle rows — newest first; `before` pages strictly older
 * entries. Carries the Wave-3c tracer filters (actorUserId / action /
 * targetType / from / to) as strict, clinic-vocabulary comma-lists
 * (admin/auditVocab.ts). Wave 4 §3: a CLINICIAN caller may read too, scoped to
 * `actorUserId=self` — the server enforces the scope; the My-stats
 * "My activity log" panel only ever asks for its own rows.
 */
export function getClinicAudit(params: AuditQuery = {}): Promise<AuditListResponse> {
  return get(`/v1/clinic/admin/audit${queryString(params)}`, true);
}
